import { useFormContext, useWatch } from "react-hook-form";

import DocumentationIconButton from "components/DocumentationIconButton";
import { ArrowLeft, ArrowRight } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";

import { NewScanScheduleContext } from "./context";
import { getMissingScanRequirements } from "./helpers";
import { NewScanScheduleStep } from "./types";

type NewScanScheduleFooterProps = {
  onFinish: () => void;
  isSubmitting?: boolean;
};

const NewScanScheduleFooter = ({ onFinish, isSubmitting }: NewScanScheduleFooterProps) => {
  const { currentStep, currentStepIndex, goToNextStep, goToPreviousStep, formOptions } =
    useTypedContext(NewScanScheduleContext);
  const { control, trigger } = useFormContext();
  const space = useWatch({ control, name: "space" });

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStep === NewScanScheduleStep.Summary;

  const { hasNoAttachableIntegration, hasNoAttachableWorkerPool } = getMissingScanRequirements(
    formOptions,
    space
  );
  const hasMissingRequirements =
    (currentStep === NewScanScheduleStep.AttachProviders && hasNoAttachableIntegration) ||
    (currentStep === NewScanScheduleStep.AttachWorkerPool && hasNoAttachableWorkerPool);

  const handleContinue = async () => {
    if (hasMissingRequirements) {
      return;
    }

    const isValid = await trigger();

    if (isValid) {
      goToNextStep();
    }
  };

  return (
    <FullScreenModalFooter justify="between">
      <Box gap="medium">
        <DocumentationIconButton
          href={getDocsUrl("/concepts/resources#account-level-resources")}
          tooltipText="Open documentation"
        />
      </Box>
      <Box direction="row" gap="medium">
        {!isFirstStep && (
          <Button
            variant="secondary"
            onPress={goToPreviousStep}
            startIcon={({ className }) => <Icon src={ArrowLeft} className={className} />}
          >
            Back
          </Button>
        )}

        {isLastStep ? (
          <Button variant="primary" onPress={onFinish} loading={isSubmitting}>
            Finish
          </Button>
        ) : (
          <Tooltip
            active={hasMissingRequirements}
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                onPress={handleContinue}
                disabled={hasMissingRequirements}
                endIcon={({ className }) => <Icon src={ArrowRight} className={className} />}
              >
                Continue
              </Button>
            )}
          >
            Set up the missing requirements in this space before continuing.
          </Tooltip>
        )}
      </Box>
    </FullScreenModalFooter>
  );
};

export default NewScanScheduleFooter;
